// Spec 26 time-attendance — PTO balance accrual + request lifecycle +
// balance impact calculation.
//
// State graph per entity_pto_balance.lifecycle_states:
//
//   active ──→ suspended ──→ active (unsuspend)
//      ↓           ↓
//   closed       closed
//   (terminal)
//
// State graph per entity_pto_request.lifecycle_states:
//
//   draft ──→ submitted ──→ approved ──→ taken (terminal)
//     ↓            ↓           ↓
//   cancelled   rejected   cancelled (pre-taken rollback)
//   (terminal) (terminal)
//
// Accepted transition rules (pto balance):
//   active    → suspended, closed
//   suspended → active, closed
//   closed    → (terminal)
//
// Accepted transition rules (pto request):
//   draft      → submitted, cancelled
//   submitted  → approved, rejected, cancelled
//   approved   → taken, cancelled
//   rejected   → (terminal)
//   cancelled  → (terminal)
//   taken      → (terminal)

import { ACCRUAL_PERIODS } from "./entities.ts";
import type {
  PtoBalance,
  PtoBalanceStatus,
  PtoRequest,
  PtoRequestStatus,
  PartialDaySpec,
} from "./entities.ts";

// ==========================================================================
// BALANCE LIFECYCLE
// ==========================================================================

const BALANCE_TRANSITIONS: Readonly<
  Record<PtoBalanceStatus, readonly PtoBalanceStatus[]>
> = {
  active: ["suspended", "closed"],
  suspended: ["active", "closed"],
  closed: [],
};

export interface PtoBalanceTransitionResult {
  readonly valid: boolean;
  readonly reason?: string;
}

export function validatePtoBalanceTransition(input: {
  readonly from: PtoBalanceStatus;
  readonly to: PtoBalanceStatus;
}): PtoBalanceTransitionResult {
  if (input.from === input.to) {
    return {
      valid: false,
      reason: `same-status transition rejected: ${input.from} → ${input.to}`,
    };
  }
  const allowed = BALANCE_TRANSITIONS[input.from];
  if (!allowed.includes(input.to)) {
    return {
      valid: false,
      reason: `transition ${input.from} → ${input.to} is not permitted; allowed: [${allowed.join(", ")}]`,
    };
  }
  return { valid: true };
}

// ==========================================================================
// REQUEST LIFECYCLE
// ==========================================================================

const REQUEST_TRANSITIONS: Readonly<
  Record<PtoRequestStatus, readonly PtoRequestStatus[]>
> = {
  draft: ["submitted", "cancelled"],
  submitted: ["approved", "rejected", "cancelled"],
  approved: ["taken", "cancelled"],
  rejected: [],
  cancelled: [],
  taken: [],
};

export interface PtoRequestTransitionResult {
  readonly valid: boolean;
  readonly reason?: string;
}

export function validatePtoRequestTransition(input: {
  readonly from: PtoRequestStatus;
  readonly to: PtoRequestStatus;
}): PtoRequestTransitionResult {
  if (input.from === input.to) {
    return {
      valid: false,
      reason: `same-status transition rejected: ${input.from} → ${input.to}`,
    };
  }
  const allowed = REQUEST_TRANSITIONS[input.from];
  if (!allowed.includes(input.to)) {
    return {
      valid: false,
      reason: `transition ${input.from} → ${input.to} is not permitted; allowed: [${allowed.join(", ")}]`,
    };
  }
  return { valid: true };
}

export function isTerminalPtoRequestStatus(status: PtoRequestStatus): boolean {
  return REQUEST_TRANSITIONS[status].length === 0;
}

// ==========================================================================
// ACCRUAL
// ==========================================================================

export interface AccrualInput {
  readonly balance: PtoBalance;
  readonly hoursWorkedThisPeriod?: number;
}

export interface AccrualResult {
  readonly newBalance: number;
  readonly accrued: number;
  readonly haltedAtCap: boolean;
}

/**
 * Compute a single period's accrual. Applies:
 *   - accrualRateHoursPerPeriod fixed (per_pay_period / monthly /
 *     quarterly / annual)
 *   - accrualRateHoursPerPeriod × hoursWorkedThisPeriod for
 *     per_hour_worked (rate interpreted as hours accrued per hour
 *     worked, e.g., 0.04 = 1h per 25 worked)
 *   - capHours halts accrual (newBalance clamps at cap; accrued value
 *     reflects the actual credited amount after clamp)
 *
 * Accrual halts when:
 *   - balance.status !== "active" → 0 accrual
 *   - accrualRate undefined → 0 accrual
 *   - balance already at cap → 0 accrual, haltedAtCap=true
 */
export function computeAccrual(input: AccrualInput): AccrualResult {
  const { balance, hoursWorkedThisPeriod } = input;

  if (balance.status !== "active") {
    return { newBalance: balance.balanceHours, accrued: 0, haltedAtCap: false };
  }
  if (balance.accrualRateHoursPerPeriod === undefined) {
    return { newBalance: balance.balanceHours, accrued: 0, haltedAtCap: false };
  }

  const rawAccrued =
    balance.accrualPeriod === "per_hour_worked"
      ? balance.accrualRateHoursPerPeriod * (hoursWorkedThisPeriod ?? 0)
      : balance.accrualRateHoursPerPeriod;

  if (rawAccrued <= 0) {
    return { newBalance: balance.balanceHours, accrued: 0, haltedAtCap: false };
  }

  const proposedNew = balance.balanceHours + rawAccrued;

  if (balance.capHours !== undefined && balance.balanceHours >= balance.capHours) {
    return { newBalance: balance.balanceHours, accrued: 0, haltedAtCap: true };
  }

  if (balance.capHours !== undefined && proposedNew > balance.capHours) {
    const accrued = balance.capHours - balance.balanceHours;
    return { newBalance: balance.capHours, accrued, haltedAtCap: true };
  }

  return { newBalance: proposedNew, accrued: rawAccrued, haltedAtCap: false };
}

// ==========================================================================
// REQUEST BALANCE IMPACT
// ==========================================================================

export interface BalanceImpactResult {
  readonly sufficient: boolean;
  readonly impactHours: number;
  readonly projectedBalance: number;
  readonly shortfallHours: number;
}

/**
 * Bir izin türünün bakiyeye nasıl davrandığı (LT-06). Tür `entity_leave_type`
 * satırından gelir; tür kaydı olmayan (0227 öncesi) bir politika için çağıran
 * {@link LEGACY_BALANCE_RULE} verir ve davranış değişmez.
 */
export interface LeaveBalanceRule {
  /**
   * false = talep bakiyeden hiç çekmez ve bakiye satırı GEREKMEZ. Ücretsiz izin
   * bir hak değildir: işçinin "ücretsiz izin bakiyesi" yoktur, 4857 böyle bir
   * bakiye şart koşmaz. Çekmeyen talebin iadesi de yoktur (hayalet saat olur).
   */
  readonly drawsBalance: boolean;
  /**
   * true = bakiye sıfırın altına inebilir (avans izin). Şirket politikasıdır ve
   * varsayılan KAPALIDIR; yasal yıllık izinde açılması İK'nın bilinçli kararıdır.
   */
  readonly allowNegative: boolean;
}

/** Tür kaydı olmayan politikanın kuralı: hep çek, sıfırın altına inme. */
export const LEGACY_BALANCE_RULE: LeaveBalanceRule = { drawsBalance: true, allowNegative: false };

/** `entity_leave_type` alanlarından bakiye kuralı; tür yoksa eski davranış. */
export function leaveBalanceRule(
  type: { readonly paid: boolean; readonly negativeBalanceAllowed: boolean } | null,
): LeaveBalanceRule {
  if (type === null) return LEGACY_BALANCE_RULE;
  return { drawsBalance: type.paid, allowNegative: type.paid && type.negativeBalanceAllowed };
}

/**
 * Given a request + current balance, compute whether the balance can
 * absorb the request. Returns `sufficient: false` when impact > balance
 * with a `shortfallHours` count. With `rule.allowNegative` the request is
 * always sufficient and `projectedBalance` may be below zero — the balance is
 * an advance the tenant chose to permit.
 *
 * Uses `request.hours` as the authoritative impact. That figure is derived by
 * the server at request-write time from the window and the holiday calendar
 * (leave-duration.ts, SEM-7211) — it is not a number the client chose.
 *
 * `rule.drawsBalance === false` is the CALLER's branch (no balance row exists to
 * pass in); calling this for such a type is a bug, not a no-op.
 */
export function computeRequestBalanceImpact(
  request: PtoRequest,
  balance: PtoBalance,
  rule: LeaveBalanceRule = LEGACY_BALANCE_RULE,
): BalanceImpactResult {
  if (!rule.drawsBalance) {
    throw new Error("computeRequestBalanceImpact: a type that draws no balance has no impact to compute");
  }
  const impactHours = request.hours;
  const projectedBalance = balance.balanceHours - impactHours;
  const sufficient = rule.allowNegative || projectedBalance >= 0;
  const shortfallHours = sufficient ? 0 : Math.abs(projectedBalance);
  return {
    sufficient,
    impactHours,
    projectedBalance: sufficient ? projectedBalance : 0,
    shortfallHours,
  };
}

export interface BalanceRestoreResult {
  readonly restoredBalance: number;
}

/**
 * Symmetric inverse of computeRequestBalanceImpact: cancelling an already-
 * approved request must credit back exactly the hours the approve-time draw
 * took, or the balance leaks permanently. No re-clamp against capHours here —
 * the cap only gates accrual (computeAccrual's haltedAtCap), not a draw
 * reversal.
 */
export function computeRequestBalanceRestore(
  request: PtoRequest,
  balance: PtoBalance,
): BalanceRestoreResult {
  return { restoredBalance: balance.balanceHours + request.hours };
}

// ==========================================================================
// FIELD VALIDATION
// ==========================================================================

export interface PtoBalanceFieldsResult {
  readonly valid: boolean;
  readonly issues: readonly string[];
}

export function validatePtoBalanceFields(balance: PtoBalance): PtoBalanceFieldsResult {
  const issues: string[] = [];

  if (balance.balanceId.trim().length === 0) issues.push("balanceId must be non-empty");
  if (balance.tenantId.trim().length === 0) issues.push("tenantId must be non-empty");
  if (balance.employeeId.trim().length === 0) issues.push("employeeId must be non-empty");
  if (balance.policyId.trim().length === 0) issues.push("policyId must be non-empty");

  if (!Number.isFinite(balance.balanceHours) || balance.balanceHours < 0) {
    issues.push(`balanceHours must be a non-negative finite number; got ${balance.balanceHours}`);
  }
  if (
    balance.accrualRateHoursPerPeriod !== undefined &&
    (!Number.isFinite(balance.accrualRateHoursPerPeriod) ||
      balance.accrualRateHoursPerPeriod < 0)
  ) {
    issues.push(
      `accrualRateHoursPerPeriod must be non-negative finite; got ${balance.accrualRateHoursPerPeriod}`,
    );
  }
  if (
    balance.accrualPeriod !== undefined &&
    !(ACCRUAL_PERIODS as readonly string[]).includes(balance.accrualPeriod)
  ) {
    issues.push(
      `accrualPeriod "${balance.accrualPeriod}" is not a valid enum value; allowed: [${ACCRUAL_PERIODS.join(", ")}]`,
    );
  }
  if (
    balance.capHours !== undefined &&
    (!Number.isFinite(balance.capHours) || balance.capHours < 0)
  ) {
    issues.push(`capHours must be non-negative finite; got ${balance.capHours}`);
  }
  if (
    balance.capHours !== undefined &&
    balance.balanceHours > balance.capHours + 0.01 // float tolerance
  ) {
    issues.push(
      `balanceHours (${balance.balanceHours}) exceeds capHours (${balance.capHours}) — invariant violation`,
    );
  }

  return { valid: issues.length === 0, issues };
}

export interface PtoRequestFieldsResult {
  readonly valid: boolean;
  readonly issues: readonly string[];
}

/**
 * True only for a real proleptic-Gregorian calendar day in the API's canonical
 * `YYYY-MM-DD` representation. `Date.parse` is deliberately not suitable:
 * JavaScript normalises values such as `2026-02-30` into March instead of
 * rejecting them, while PostgreSQL's DATE input rightly rejects the original
 * wire value. Keep this pure helper in the SDK so every PTO producer applies
 * the same calendar rule before persistence.
 */
export function isStrictIsoCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;

  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= daysInMonth[month - 1]!;
}

/** Whole local-clock minutes; no dates, timezone conversion or client hours. */
export function partialDayMinutes(window: PartialDaySpec): number | null {
  const clock = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
  if (!clock.test(window.startTime) || !clock.test(window.endTime)) return null;
  const minutes = (value: string): number => Number(value.slice(0, 2)) * 60 + Number(value.slice(3));
  const duration = minutes(window.endTime) - minutes(window.startTime);
  return duration > 0 ? duration : null;
}

export function validatePtoRequestFields(request: PtoRequest): PtoRequestFieldsResult {
  const issues: string[] = [];

  if (request.requestId.trim().length === 0) issues.push("requestId must be non-empty");
  if (request.tenantId.trim().length === 0) issues.push("tenantId must be non-empty");
  if (request.employeeId.trim().length === 0) issues.push("employeeId must be non-empty");
  if (request.policyId.trim().length === 0) issues.push("policyId must be non-empty");

  const startDateValid = isStrictIsoCalendarDate(request.startDate);
  const endDateValid = isStrictIsoCalendarDate(request.endDate);
  if (!startDateValid) issues.push(`startDate "${request.startDate}" invalid`);
  if (!endDateValid) issues.push(`endDate "${request.endDate}" invalid`);
  // Canonical YYYY-MM-DD strings sort chronologically, avoiding Date.parse's
  // normalisation of impossible calendar days.
  if (startDateValid && endDateValid && request.endDate < request.startDate) {
    issues.push("endDate must be ≥ startDate");
  }

  if (!Number.isFinite(request.hours) || request.hours <= 0) {
    issues.push(`hours must be positive finite; got ${request.hours}`);
  }

  if (request.partialDays !== undefined) {
    for (const [idx, pd] of request.partialDays.entries()) {
      if (!isStrictIsoCalendarDate(pd.date)) {
        issues.push(`partialDays[${idx}].date "${pd.date}" invalid`);
      }
      if (partialDayMinutes(pd) === null) {
        issues.push(
          `partialDays[${idx}] must have ordered same-day HH:MM startTime and endTime`,
        );
      }
    }
  }

  // Status-dependent invariants
  if (request.status === "submitted" && request.submittedAt === undefined) {
    issues.push("submittedAt is required when status=submitted");
  }
  if (
    (request.status === "approved" || request.status === "rejected") &&
    request.decidedAt === undefined
  ) {
    issues.push(`decidedAt is required when status=${request.status}`);
  }
  if (request.status === "cancelled" && request.cancelledAt === undefined) {
    issues.push("cancelledAt is required when status=cancelled");
  }
  if (request.status === "taken" && request.takenAt === undefined) {
    issues.push("takenAt is required when status=taken");
  }

  return { valid: issues.length === 0, issues };
}
